import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { NpiProvider, NpiApiResponse } from './npi-provider.model';

/**
 * Service for querying the NIH Clinical Table Search Service — NPI Individuals endpoint.
 *
 * API Base URL: https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search
 * - Free, no API key required
 * - No CORS issues (public API with CORS headers)
 * - 7.1M+ provider records, updated monthly from CMS
 * - Supports search by name, NPI number, provider type, and address
 *
 * For organizations use: /api/npi_org/v3/search (same field structure)
 */
@Injectable({ providedIn: 'root' })
export class NpiLookupService {

  private readonly API_BASE = 'https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search';

  /**
   * Fields shown in the autocomplete dropdown.
   * df = display fields (returned as string arrays in index [3])
   */
  private readonly DISPLAY_FIELDS = [
    'NPI',
    'name.full',
    'provider_type',
    'addr_practice.full'
  ].join(',');

  /**
   * Extra fields returned as structured data in index [2].
   * These populate the detailed provider card after selection.
   */
  private readonly EXTRA_FIELDS = [
    'name.first',
    'name.last',
    'name.credential',
    'gender',
    'addr_practice.phone',
    'addr_practice.fax',
    'addr_practice.city',
    'addr_practice.state',
    'addr_practice.zip'
  ].join(',');

  /**
   * Fields that are searched against the query term.
   * Includes NPI so users can paste an NPI number directly.
   */
  private readonly SEARCH_FIELDS = [
    'NPI',
    'name.full',
    'provider_type',
    'addr_practice.full'
  ].join(',');

  constructor(private http: HttpClient) {}

  /**
   * Search for individual NPI providers.
   *
   * @param term  - Search string (name, NPI, specialty, city, etc.)
   * @param maxResults - Number of results to return (max 500)
   * @returns Observable of parsed NpiProvider[]
   *
   * @example
   *   this.npiService.search('Smith cardiology Atlanta').subscribe(providers => { ... });
   *   this.npiService.search('1234567890').subscribe(providers => { ... }); // search by NPI
   */
  search(term: string, maxResults: number = 500): Observable<NpiProvider[]> {
    const params = new HttpParams()
      .set('terms', term)
      .set('maxList', maxResults.toString())
      .set('df', this.DISPLAY_FIELDS)
      .set('sf', this.SEARCH_FIELDS)
      .set('ef', this.EXTRA_FIELDS);

    return this.http.get<NpiApiResponse>(this.API_BASE, { params }).pipe(
      map(response => this.parseResponse(response))
    );
  }

  /**
   * Search with an additional constraint query (Elasticsearch query_string syntax).
   * Useful for filtering by state, provider type, etc.
   *
   * @example
   *   // Find cardiologists in Georgia
   *   this.npiService.searchConstrained('cardiology', 'addr_practice.state:GA');
   */
  searchConstrained(term: string, constraint: string, maxResults: number = 500): Observable<NpiProvider[]> {
    const params = new HttpParams()
      .set('terms', term)
      .set('maxList', maxResults.toString())
      .set('df', this.DISPLAY_FIELDS)
      .set('sf', this.SEARCH_FIELDS)
      .set('ef', this.EXTRA_FIELDS)
      .set('q', constraint);

    return this.http.get<NpiApiResponse>(this.API_BASE, { params }).pipe(
      map(response => this.parseResponse(response))
    );
  }

  /**
   * Look up a single provider by exact NPI number.
   * Returns null if not found.
   */
  getByNpi(npi: string): Observable<NpiProvider | null> {
    return this.search(npi, 1).pipe(
      map(providers => {
        const match = providers.find(p => p.npi === npi);
        return match ?? null;
      })
    );
  }

  /**
   * Parse the raw CTSS array response into typed NpiProvider objects.
   *
   * Response format:
   *   [0] = total result count
   *   [1] = string[] of NPI codes
   *   [2] = { field_name: value[] } extra fields hash
   *   [3] = string[][] display field arrays
   */
  private parseResponse(response: NpiApiResponse): NpiProvider[] {
    const [_total, npiCodes, extraFields, displayRows] = response;

    if (!npiCodes || npiCodes.length === 0) {
      return [];
    }

    return npiCodes.map((npi, i) => {
      const display = displayRows?.[i] ?? [];
      const ef = extraFields ?? {};

      return {
        npi,
        fullName:        display[1] ?? '',
        providerType:    display[2] ?? '',
        practiceAddress: display[3] ?? '',
        firstName:       ef['name.first']?.[i] ?? null,
        lastName:        ef['name.last']?.[i] ?? null,
        credential:      ef['name.credential']?.[i] ?? null,
        gender:          ef['gender']?.[i] ?? null,
        phone:           ef['addr_practice.phone']?.[i] ?? null,
        fax:             ef['addr_practice.fax']?.[i] ?? null,
        city:            ef['addr_practice.city']?.[i] ?? null,
        state:           ef['addr_practice.state']?.[i] ?? null,
        zip:             ef['addr_practice.zip']?.[i] ?? null,
      };
    });
  }
}
